import { useState, useCallback, useEffect } from 'react';
import { Check, Droplets, FlaskConical, AlertCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Burette, Beaker } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Transferring the Aliquot',
    content: 'A 10.00 mL aliquot of diluted vinegar is pipetted into each of three 250 mL Erlenmeyer flasks. Use a volumetric pipette for accuracy. The Erlenmeyer flask shape allows swirling without spillage during titration.',
    formulas: ['Aliquot volume = 10.00 mL', 'Original vinegar in aliquot = 10.00/100 x 10 = 1.0 mL']
  },
  2: {
    title: 'Phenolphthalein Indicator',
    content: 'Phenolphthalein is colorless in acidic solutions (pH < 8.2) and pink in basic solutions (pH > 10). Only two drops are needed. At the equivalence point of this titration, the solution becomes slightly basic, triggering the color change.',
    formulas: ['pKa of phenolphthalein = 9.4', 'Color change range: pH 8.2-10.0']
  },
  3: {
    title: 'Titrating to the Endpoint',
    content: 'Add NaOH steadily while swirling until a faint pink color appears. Near the endpoint, add dropwise and watch for the first pale pink colour that persists for 30 seconds. This indicates all the acetic acid has been neutralized.',
    formulas: ['CH3COOH + NaOH -> CH3COONa + H₂O', 'mol NaOH = mol CH3COOH (1:1 ratio)', 'M_acid x V_acid = M_base x V_base']
  }
};

export default function Part3Titration() {
  const [aliquotTransferred, setAliquotTransferred] = useState(false);
  const [indicatorAdded, setIndicatorAdded] = useState(false);
  const [titrating, setTitrating] = useState(false);
  const [endpointReached, setEndpointReached] = useState(false);
  const [buretteReading, setBuretteReading] = useState(0.85);
  const [finalReading, setFinalReading] = useState(0);
  const [solutionColor, setSolutionColor] = useState('#FFFFFF');
  const [currentStep, setCurrentStep] = useState(1);
  const [stopcockOpen, setStopcockOpen] = useState(false);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [aliquotTransferred, indicatorAdded, endpointReached].filter(Boolean).length;

  // Titration simulation
  useEffect(() => {
    if (titrating && stopcockOpen && !endpointReached) {
      const interval = setInterval(() => {
        setBuretteReading(prev => {
          const newReading = prev + 0.15;
          // Endpoint around 20-22 mL
          const targetVolume = 20.5 + Math.random() * 1.5;

          if (newReading >= targetVolume) {
            setEndpointReached(true);
            setFinalReading(parseFloat(newReading.toFixed(2)));
            setSolutionColor('#FFB6C1'); // Light pink - endpoint
            setStopcockOpen(false);
            setTitrating(false);
            return newReading;
          }

          // Gradual color change as we approach endpoint
          const progress = newReading / targetVolume;
          if (progress > 0.95) {
            setSolutionColor('#FFCDD2'); // Very light pink
          }

          return newReading;
        });
      }, 200);

      return () => clearInterval(interval);
    }
  }, [titrating, stopcockOpen, endpointReached]);

  const handleTransferAliquot = useCallback(() => {
    if (!aliquotTransferred) {
      setAliquotTransferred(true);
      setSolutionColor('#FFFACD'); // Pale yellow (diluted vinegar)
      setCurrentStep(2);
    }
  }, [aliquotTransferred]);

  const handleAddIndicator = useCallback(() => {
    if (aliquotTransferred && !indicatorAdded) {
      setIndicatorAdded(true);
      setCurrentStep(3);
    }
  }, [aliquotTransferred, indicatorAdded]);

  const handleToggleStopcock = useCallback(() => {
    if (indicatorAdded && !endpointReached) {
      setStopcockOpen(prev => !prev);
      setTitrating(true);
    }
  }, [indicatorAdded, endpointReached]);

  const volumeUsed = finalReading - 0.85;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part III: Titration</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Transfer Aliquot', hint: 'Pipette 10.00 mL to flask', done: aliquotTransferred },
            { id: 2, title: 'Add Indicator', hint: 'Add phenolphthalein drops', done: indicatorAdded },
            { id: 3, title: 'Titrate to Endpoint', hint: 'Add NaOH until pink', done: endpointReached }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Results Display */}
        {endpointReached && (
          <div className="results-panel">
            <h4>Titration Results</h4>
            <div className="result-row">
              <span>Initial Reading:</span>
              <span className="value">0.85 mL</span>
            </div>
            <div className="result-row">
              <span>Final Reading:</span>
              <span className="value">{finalReading.toFixed(2)} mL</span>
            </div>
            <div className="result-row highlight">
              <span>Volume NaOH:</span>
              <span className="value">{volumeUsed.toFixed(2)} mL</span>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Vinegar Titration"
        completedCount={completedSteps}
        totalSteps={3}
        equipment={['Burette', 'Erlenmeyer flask', 'Phenolphthalein']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="titration-workspace">
          <div className="titration-setup">
            {/* Burette */}
            <div className="burette-section">
              <Burette
                volume={50 - buretteReading}
                maxVolume={50}
                liquidColor="#E3F2FD"
                stopcock={stopcockOpen ? 'open' : 'closed'}
                onStopcock={indicatorAdded && !endpointReached ? handleToggleStopcock : undefined}
              />
              <div className="burette-reading">
                Reading: {buretteReading.toFixed(2)} mL
              </div>
            </div>

            {/* Erlenmeyer Flask */}
            <div
              className={`flask-section ${currentStep <= 2 && !indicatorAdded ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 1) handleTransferAliquot();
                else if (currentStep === 2) handleAddIndicator();
              }}
            >
              <svg viewBox="0 0 120 150" width={150} height={180}>
                <defs>
                  <linearGradient id="flask-glass" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                    <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                  </linearGradient>
                </defs>

                {/* Flask body */}
                <path
                  d="M40 20 L40 50 L10 120 Q10 140 60 140 Q110 140 110 120 L80 50 L80 20"
                  fill="url(#flask-glass)"
                  stroke="#7A9BB8"
                  strokeWidth="2"
                />

                {/* Liquid */}
                {aliquotTransferred && (
                  <path
                    d="M18 115 Q18 135 60 135 Q102 135 102 115 L85 55 L35 55 Z"
                    fill={solutionColor}
                    opacity="0.9"
                  >
                    {titrating && (
                      <animate
                        attributeName="opacity"
                        values="0.9;0.7;0.9"
                        dur="0.5s"
                        repeatCount="indefinite"
                      />
                    )}
                  </path>
                )}

                {/* Neck */}
                <rect x="40" y="10" width="40" height="15" fill="url(#flask-glass)" stroke="#7A9BB8" strokeWidth="1.5" />
              </svg>
              <span className="flask-label">250 mL Erlenmeyer</span>
            </div>

            {/* Indicator Bottle */}
            <div className="indicator-bottle">
              <svg viewBox="0 0 40 80" width={50} height={90}>
                <rect x="10" y="25" width="20" height="45" rx="2" fill="#4A4A4A" />
                <rect x="12" y="30" width="16" height="35" fill="#FFB6C1" opacity="0.8" />
                <rect x="12" y="10" width="16" height="15" rx="1" fill="#333" />
                <circle cx="20" cy="73" r="2" fill="#FFB6C1" />
              </svg>
              <span className="bottle-label">Phenolphthalein</span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {!aliquotTransferred && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the flask to transfer 10.00 mL of diluted vinegar
              </div>
            )}
            {aliquotTransferred && !indicatorAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the flask to add two drops of phenolphthalein
              </div>
            )}
            {indicatorAdded && !endpointReached && (
              <div className="status-message warning">
                <AlertCircle size={20} />
                Click the stopcock to {stopcockOpen ? 'close' : 'open'} - titrate until pink color persists
              </div>
            )}
            {endpointReached && (
              <div className="status-message success">
                <Check size={20} />
                Endpoint reached! The first pale pink colour persists for 30 seconds.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Acid-Base Titration</h4>
            <p>
              CH₃COOH + NaOH → CH₃COONa + H₂O
            </p>
            <p>
              At the equivalence point, all acetic acid has reacted with NaOH.
              The phenolphthalein turns pink as the solution becomes basic.
            </p>
          </div>
        </div>

        <style>{`
          .titration-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .titration-setup {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .burette-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .burette-reading {
            background: #1a1a1a;
            padding: 6px 12px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 14px;
            color: #00ff88;
          }

          .flask-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s;
          }

          .flask-section.clickable {
            cursor: pointer;
          }

          .flask-section.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .flask-label, .bottle-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .indicator-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .results-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-top: 16px;
          }

          .results-panel h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .result-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13px;
          }

          .result-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .result-row.highlight {
            background: rgba(0, 168, 107, 0.1);
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }

          .result-row.highlight .value {
            color: var(--secondary);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.warning {
            background: rgba(251, 191, 36, 0.2);
            color: #FBBF24;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p:last-child {
            margin-bottom: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp9"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
